import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteConfig, getCanonicalUrl } from '../router/routes';

interface UseSEOOptions {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

declare global {
  function gtag(...args: unknown[]): void;
}

export const useSEO = (options: UseSEOOptions = {}) => {
  const location = useLocation();
  const routeConfig = getRouteConfig(location.pathname);
  const canonicalUrl = getCanonicalUrl(location.pathname);

  // Merge route config with custom options
  const title = options.title || routeConfig?.title || 'Stimm.DEV';
  const description = options.description || routeConfig?.description || '';
  const keywords = options.keywords || routeConfig?.keywords || '';
  const image = options.image || `${window.location.origin}/assets/images/og-image.jpg`;
  const type = options.type || 'website';
  const fullUrl = `${window.location.origin}${canonicalUrl}`;

  useEffect(() => {
    // Analytics tracking
    if (typeof gtag !== 'undefined') {
      gtag('config', 'GA_MEASUREMENT_ID', {
        page_title: title,
        page_location: fullUrl
      });
    }

    // Schema.org structured data
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Stimm.DEV",
      "url": "https://stimm.dev",
      "logo": "https://stimm.dev/assets/images/logo.png",
      "sameAs": [
        "https://github.com/stimmdev",
        "https://linkedin.com/company/stimmdev"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+49-30-42141523",
        "contactType": "customer service",
        "email": "service@stimm.dev",
        "areaServed": "DE",
        "availableLanguage": ["German", "English"]
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Berlin",
        "addressCountry": "DE",
        "addressRegion": "Berlin"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "47",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // Website schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Stimm.DEV",
      "url": "https://stimm.dev",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://stimm.dev/suche?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    };

    // Service schema für bessere Rich Snippets
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "App- und Webentwicklung",
      "description": "Professionelle Entwicklung von Mobile Apps und Webanwendungen",
      "provider": {
        "@type": "Organization",
        "name": "Stimm.DEV",
        "url": "https://stimm.dev"
      },
      "areaServed": "Berlin, Deutschland",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Entwicklungsdienstleistungen",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Mobile App Entwicklung",
              "description": "iOS und Android Apps"
            },
            "priceRange": "€€€"
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Webentwicklung mit React",
              "description": "Moderne Webanwendungen"
            },
            "priceRange": "€€€"
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "47",
        "bestRating": "5"
      }
    };

    // Inject structured data
    const scriptId = 'structured-data';
    const existingScript = document.getElementById(scriptId);
    
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify([organizationSchema, websiteSchema, serviceSchema]);
    document.head.appendChild(script);
  }, [title, fullUrl]);

  return {
    title,
    description,
    keywords,
    image,
    type,
    fullUrl,
    routeConfig
  };
};
